import { type GameModule, type SimState, tick } from '@md/engine';
import {
  type DuoInputs,
  NEUTRAL_INPUTS,
  type PlayerIndex,
  type PlayerInputs,
  PREDICTION_MAX_PENDING_INPUTS,
} from '@md/shared';

/**
 * Prédiction locale et réconciliation (spec §5.2).
 *
 * Le client applique ses entrées immédiatement sur SON robot (prédiction) puis,
 * à chaque snapshot, réécrit l'état à partir de l'autorité et **rejoue** les
 * entrées non encore confirmées (`seq > lastSeq`). Comme le `tick()` du moteur
 * est pur et déterministe et qu'il est partagé avec le serveur, le rejeu
 * reproduit exactement ce que le serveur produira.
 *
 * Ce module ne touche ni au DOM ni au réseau : il est testé en Vitest.
 */

/** Une entrée envoyée au serveur et pas encore confirmée. */
export interface PendingInput {
  readonly seq: number;
  /** Tick de simulation auquel l'entrée a été échantillonnée. */
  readonly tick: number;
  readonly inputs: PlayerInputs;
}

export interface PredictionBuffer {
  /** Mémorise une entrée émise. */
  record(input: PendingInput): void;
  /** Oublie tout ce que le serveur a confirmé (`seq <= lastSeq`). */
  ack(lastSeq: number): void;
  pending(): readonly PendingInput[];
  size(): number;
  clear(): void;
}

export function createPredictionBuffer(max = PREDICTION_MAX_PENDING_INPUTS): PredictionBuffer {
  let buffer: PendingInput[] = [];
  return {
    record(input: PendingInput): void {
      buffer.push(input);
      if (buffer.length > max) {
        buffer = buffer.slice(buffer.length - max);
      }
    },
    ack(lastSeq: number): void {
      buffer = buffer.filter((entry) => entry.seq > lastSeq);
    },
    pending(): readonly PendingInput[] {
      return buffer;
    },
    size(): number {
      return buffer.length;
    },
    clear(): void {
      buffer = [];
    },
  };
}

export interface ReconcileOptions {
  /** État autoritaire reçu du serveur (tick T). */
  readonly confirmed: SimState;
  /** Entrées locales non confirmées, dans l'ordre d'émission. */
  readonly pending: readonly PendingInput[];
  /** Place du robot piloté localement. */
  readonly localIndex: PlayerIndex;
  /** Dernières entrées connues du partenaire — maintenues pendant le rejeu. */
  readonly partnerInputs: PlayerInputs;
  readonly module: GameModule;
  /** Tick auquel le client veut se retrouver après rejeu. */
  readonly targetTick: number;
  /** Garde-fou : nombre maximal de ticks rejoués en une fois. */
  readonly maxReplayTicks?: number;
}

/**
 * Entrées locales à appliquer pour produire le tick `t + 1` : la dernière
 * entrée émise à un tick ≤ `t` (exactement la règle « le dernier reçu gagne »
 * appliquée par le serveur).
 */
function inputsAtTick(
  pending: readonly PendingInput[],
  tickValue: number,
  fallback: PlayerInputs,
): PlayerInputs {
  let chosen: PlayerInputs = fallback;
  for (const entry of pending) {
    if (entry.tick <= tickValue) {
      chosen = entry.inputs;
    } else {
      break;
    }
  }
  return chosen;
}

/**
 * Rejoue les entrées non confirmées par-dessus l'état autoritaire.
 *
 * Renvoie l'état prédit au tick `targetTick` (ou `confirmed` si le client est
 * déjà en retard sur le serveur, auquel cas il se recale simplement).
 */
export function reconcile(options: ReconcileOptions): SimState {
  const { confirmed, pending, localIndex, partnerInputs, module } = options;
  const maxReplay = options.maxReplayTicks ?? PREDICTION_MAX_PENDING_INPUTS;
  const target = Math.min(options.targetTick, confirmed.tick + maxReplay);
  if (target <= confirmed.tick) {
    return confirmed;
  }

  const fallback = confirmed.inputs[localIndex] ?? NEUTRAL_INPUTS;
  let state = confirmed;
  for (let t = confirmed.tick; t < target; t += 1) {
    const local = inputsAtTick(pending, t, fallback);
    const duo: DuoInputs = localIndex === 0 ? [local, partnerInputs] : [partnerInputs, local];
    state = tick(state, duo, module);
  }
  return state;
}
